import { createClient } from '@supabase/supabase-js';

const SESSION_CONFIG = {
  autoRefreshToken: true,
  persistSession: true,
  detectSessionInUrl: true,
  flowType: 'implicit',
  // Keep session alive for 7 days
  localStorage: {
    expiryDays: 7
  },
  // Refresh token 5 minutes before expiry
  refreshTokenThreshold: 300
};

const RETRY_CONFIG = {
  retries: 3,
  retryDelay: (retryCount: number) => Math.min(1000 * Math.pow(2, retryCount), 10000),
  shouldRetry: (error: any) => {
    return error?.message?.includes('Failed to fetch') || 
           error?.message?.includes('network') ||
           error?.status === 503 ||
           error?.status === 429;
  }
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Custom fetch implementation with retry logic
const fetchWithRetry: typeof fetch = async (input, init) => {
  let lastError;
  
  for (let i = 0; i < RETRY_CONFIG.retries; i++) {
    try {
      const response = await fetch(input, init);
      if (!response.ok && RETRY_CONFIG.shouldRetry({ status: response.status })) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response;
    } catch (error) {
      lastError = error;
      if (i < RETRY_CONFIG.retries - 1 && RETRY_CONFIG.shouldRetry(error)) {
        const delay = RETRY_CONFIG.retryDelay(i);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      break;
    }
  }
  throw lastError;
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: SESSION_CONFIG,
  global: {
    fetch: fetchWithRetry
  }
});

// Add error event listener for global error tracking
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    console.log('User signed out, clearing session');
    supabase.auth.refreshSession(); // Attempt to refresh the session
  }
});